"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import CountryFlag from "@/components/ui/CountryFlag";
import { Search, Tag } from "lucide-react";
import { slugify } from "@/lib/utils/slugify";

interface GlossaryItem {
  term: string;
  definition: string;
  category: string;
  localName?: string;
  relatedTerms?: string[];
  explanation?: string;
  keyTakeaways?: string[];
  calculatorLink?: string;
}

interface GlossarySearchClientProps {
  country: string;
  countryName: string;
  glossary: GlossaryItem[];
}

export default function GlossarySearchClient({ country, countryName, glossary }: GlossarySearchClientProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Get unique categories for filter badges
  const categories = useMemo(() => {
    const cats = new Set(glossary.map((item) => item.category));
    return ["All", ...Array.from(cats).sort()];
  }, [glossary]);

  // Filter terms in real-time
  const filteredGlossary = useMemo(() => {
    return glossary.filter((item) => {
      const matchesSearch =
        item.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.definition.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === "All" || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [glossary, searchQuery, selectedCategory]);

  // Group terms alphabetically
  const groupedTerms = useMemo(() => {
    const grouped: Record<string, GlossaryItem[]> = {};
    filteredGlossary.forEach((item) => {
      const firstLetter = item.term.charAt(0).toUpperCase();
      const groupKey = /^[A-Z]$/.test(firstLetter) ? firstLetter : "#";
      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }
      grouped[groupKey].push(item);
    });
    return grouped;
  }, [filteredGlossary]);

  const alphabet = useMemo(() => {
    return Object.keys(groupedTerms).sort((a, b) => {
      if (a === "#") return 1;
      if (b === "#") return -1;
      return a.localeCompare(b);
    });
  }, [groupedTerms]);

  return (
    <div className="glossary-layout" style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      {/* Search and Filters Controls */}
      <div 
        className="card" 
        style={{ 
          padding: "var(--space-5)", 
          display: "flex", 
          flexDirection: "column", 
          gap: "var(--space-4)",
          background: "var(--bg-surface)",
          border: "1px solid var(--border-color)",
        }}
      >
        {/* Search Input */}
        <div style={{ position: "relative", width: "100%" }}>
          <Search 
            size={18} 
            style={{ 
              position: "absolute", 
              left: "1rem", 
              top: "50%", 
              transform: "translateY(-50%)", 
              color: "var(--text-muted)" 
            }} 
          />
          <input
            type="text"
            placeholder={`Search ${countryName} glossary terms (e.g. 401k, ISA, EPF)...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "0.75rem 1rem 0.75rem 2.5rem",
              background: "var(--bg-elevated)",
              border: "1px solid var(--border-color)",
              borderRadius: "var(--radius-md)",
              color: "var(--text-primary)",
              fontSize: "var(--text-sm)",
              outline: "none",
            }}
          />
        </div>

        {/* Category Filters */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center" }}>
          <span style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: "0.25rem", marginRight: "0.25rem" }}>
            <Tag size={12} /> Filter:
          </span>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              style={{
                padding: "0.35rem 0.75rem",
                borderRadius: "var(--radius-sm)",
                fontSize: "var(--text-xs)",
                fontWeight: "var(--fw-semibold)",
                border: "1px solid",
                borderColor: selectedCategory === cat ? "var(--primary)" : "var(--border-color)",
                background: selectedCategory === cat ? "var(--primary-a15)" : "var(--bg-elevated)",
                color: selectedCategory === cat ? "var(--primary)" : "var(--text-secondary)",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {filteredGlossary.length === 0 ? (
        <div className="card text-center p-8 text-muted" style={{ padding: "var(--space-8)", textAlign: "center", color: "var(--text-secondary)" }}>
          No terms matched your search. Try typing another concept!
        </div>
      ) : (
        <div className="glossary-grid-wrap" style={{ display: "grid", gridTemplateColumns: "1fr", gap: "var(--space-6)" }}>
          {/* Alphabet Shortcuts bar */}
          <div 
            className="card" 
            style={{ 
              padding: "var(--space-4)", 
              background: "var(--bg-surface)",
              border: "1px solid var(--border-color)",
            }}
          >
            {/* h2: first heading after the page h1 — h4 skipped two levels. */}
            <h2 style={{ margin: 0, fontSize: "var(--text-xs)", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-secondary)", marginBottom: "0.5rem" }}>
              Jump to letter
            </h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
              {alphabet.map((letter) => (
                <a
                  key={letter}
                  href={`#letter-${letter}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "28px",
                    height: "28px",
                    borderRadius: "var(--radius-sm)",
                    background: "var(--bg-elevated)",
                    color: "var(--text-primary)",
                    fontSize: "var(--text-xs)",
                    fontWeight: "var(--fw-bold)",
                    textDecoration: "none",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--primary)";
                    e.currentTarget.style.color = "var(--text-on-accent)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "var(--bg-elevated)";
                    e.currentTarget.style.color = "var(--text-primary)";
                  }}
                >
                  {letter}
                </a>
              ))}
            </div>
          </div>

          {/* List of Grouped Terms */}
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
            {alphabet.map((letter) => (
              <section key={letter} id={`letter-${letter}`} style={{ scrollMarginTop: "5rem" }}>
                <h2 
                  style={{ 
                    fontSize: "var(--text-2xl)",
                    fontWeight: "var(--fw-extrabold)",
                    color: "var(--primary)", 
                    borderBottom: "2px solid var(--border-color)", 
                    paddingBottom: "0.35rem",
                    marginBottom: "var(--space-4)" 
                  }}
                >
                  {letter}
                </h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "var(--space-4)" }}>
                  {groupedTerms[letter].map((item) => {
                    const slug = slugify(item.term);
                    return (
                      <Link 
                        key={item.term}
                        href={`/${country}/glossary/${slug}`}
                        className="card" 
                        style={{ 
                          padding: "var(--space-5)", 
                          display: "flex", 
                          flexDirection: "column", 
                          gap: "0.5rem",
                          background: "var(--bg-surface)",
                          border: "1px solid var(--border-color)",
                          transition: "transform 0.15s ease, border-color 0.15s ease",
                          cursor: "pointer",
                          textDecoration: "none",
                          color: "inherit",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.borderColor = "var(--primary)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "none";
                          e.currentTarget.style.borderColor = "var(--border-color)";
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                          <h3 
                            style={{ 
                              margin: 0, 
                              fontSize: "var(--text-lg)",
                              fontWeight: "var(--fw-extrabold)",
                              color: "var(--text-primary)",
                              lineHeight: 1.3
                            }}
                          >
                            {item.term}
                          </h3>
                          <span 
                            className="badge badge-secondary" 
                            style={{ 
                              fontSize: "var(--text-2xs)",
                              fontWeight: "var(--fw-bold)", 
                              textTransform: "uppercase", 
                              letterSpacing: "0.05em",
                              flexShrink: 0
                            }}
                          >
                            {item.category}
                          </span>
                        </div>
                        
                        <p 
                          style={{ 
                            margin: 0, 
                            fontSize: "var(--text-xs)", 
                            color: "var(--text-secondary)", 
                            lineHeight: 1.5,
                            flex: 1 
                          }}
                        >
                          {item.definition}
                        </p>
                        
                        <span 
                          style={{ 
                            fontSize: "var(--text-xs)", 
                            fontWeight: "var(--fw-bold)", 
                            color: "var(--primary)", 
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            marginTop: "0.5rem"
                          }}
                        >
                          View full definition &rarr;
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
